import {Box, TextField, Typography} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useState} from 'react';
import ConfigureIndexTable from './ConfigureIndexTable';
import AurButton from '../../../common/aur-button/AurButton';
import AurPopup from '../../../common/aur-popup/AurPopup';
import {useNavigate} from 'react-router-dom';
import {setAddCustomIndex} from '../../../../redux/slices/indexingSlice';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {createCustomIndex} from '../../../../api/EcaServices';
import AurToast from '../../../common/aur-toast/AurToast';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

interface IAnalyzerList {
  name: string;
  analyzer: string;
}
const ConfigureIndexStep4: React.FC = () => {
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [cancelBtnPopup, setCancelBtnPopup] = useState<boolean>(false);
  const [customIndexConfirm, setCustomIndexConfirm] = useState<boolean>(false);
  const [emails, setEmails] = useState('');
  const [error, setError] = useState('');
  const [isButtonDisabled, setisButtonDisabled] = useState<boolean>(true);
  const [newAnalyzersList, setNewAnalyzersList] = useState<IAnalyzerList[]>([]);
  const [apiErrorMsg, setApiErrorMsg] = useState<string>('');
  const [isOpenErrorPopup, setIsOpenErrorPopup] = useState<boolean>(false);
  const [reqLoading, setReqLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();

  const createIndexSubPage = {
    subpage_name: t('gaTracking.customIndex.createNewCustomIndexSubpage'),
    subpage_type: GA_SUBPAGE_TYPE.MODAL,
  };

  const trackCreateIndexSubPageView = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: createIndexSubPage,
    });
  };

  const notifyWhenCompleteSubPage = {
    subpage_name: t('gaTracking.customIndex.notifyWhenComplete'),
    subpage_type: GA_SUBPAGE_TYPE.MODAL,
  };

  const trackNotifyWhenCompleteSubPageView = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: notifyWhenCompleteSubPage,
    });
  };

  const trackNotifyWhenCompleteModalClick = (name: string, action: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name,
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action,
      },
      subPage: notifyWhenCompleteSubPage,
    });
  };

  const trackCreateIndexModalClick = (name: string, action: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name,
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action,
      },
      subPage: createIndexSubPage,
    });
  };

  const continueButtonHandler = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.indexManagement.continue'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t('gaTracking.indexManagement.newIndexConfigured'),
      },
      subPage: {
        subpage_name: t('gaTracking.indexManagement.configureIndex'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    setOpenPopup(true);
    setEmails('');
    setError('');
  };
  const dispatch = useDispatch();

  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );
  const indexAnalyzers = useSelector(
    (state: RootState) => state.indexes.indexAnalyzers
  );

  const indexCustom = useSelector(
    (state: RootState) => state.indexes.indexCustom
  );

  const metaFieldsValue = useSelector(
    (state: RootState) => state.indexes.selectedMetaField
  );

  useEffect(() => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.indexManagement.configureIndex'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  }, []);

  useEffect(() => {
    if (openPopup) {
      trackNotifyWhenCompleteSubPageView();
    }
  }, [openPopup]);

  const tableData = metaFieldsValue;
  const buildTypeList = indexAnalyzers;

  const cancelButtonHandler = () => {
    setCancelBtnPopup(true);
  };

  const validateEmails = (emails: any) => {
    const emailArray = emails.trim().split(' ');

    // Regex to validate email format
    const emailRegex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[\w-]{2,4}$/;

    // Check if each email is valid
    for (const email of emailArray) {
      if (!emailRegex.test(email)) {
        return false; // Return false if any email is invalid
      }
    }

    return true; // Return true if all emails are valid
  };

  const handleOkButton = () => {
    if (!validateEmails(emails)) {
      setError(t('common.invalidEmailSeparator'));
    } else {
      trackNotifyWhenCompleteModalClick(
        t('gaTracking.global.continue'),
        t('gaTracking.customIndex.notifyOnEmail')
      );
      trackCreateIndexSubPageView();
      setCustomIndexConfirm(true);
      setOpenPopup(false);
    }
  };

  const closeButtonHandler = () => {
    trackNotifyWhenCompleteModalClick(
      t('gaTracking.global.cancel'),
      t('gaTracking.customIndex.newIndexCancel')
    );
    setOpenPopup(false);
  };

  const handleChange = (e: any) => {
    setEmails(e.target.value);
    const isValid = validateEmails(e.target.value);
    if (isValid) {
      setError('');
      setisButtonDisabled(false);
    } else {
      setError(t('common.invalidEmailFormat'));
      setisButtonDisabled(true);
    }
  };

  const handleContinueOptionSelected = () => {
    setCancelBtnPopup(false);
    navigate('/indexmanagement');
  };

  const closeModalHandler = () => {
    setCancelBtnPopup(false);
  };

  const createIndexConfirmHandler = async () => {
    trackCreateIndexModalClick(
      t('gaTracking.customIndex.createCustomIndex'),
      t('gaTracking.customIndex.newIndexComplete')
    );
    setReqLoading(true);
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    const mappedSelectValue = newAnalyzersList
      .map((label) => {
        const field = metaFieldsValue.find(
          (field: any) => field.fieldLabel === label.name
        );
        const analyzer = indexAnalyzers.find(
          (analyzer: any) => analyzer.name === label.analyzer
        );

        if (field && analyzer) {
          return {analyzerId: analyzer.id, fieldId: field.fieldId};
        }
        return null;
      })
      .filter((item) => item !== null);

    const payload = {
      name: indexCustom.indexName,
      savedSearchId: Number(indexCustom.savedSearchId),
      customMetadataFieldList: mappedSelectValue,
      emailAddress: emails,
    };
    try {
      const result = await createCustomIndex(params, payload);
      if (result.meta.status === 'Success') {
        setCustomIndexConfirm(false);
        dispatch(setAddCustomIndex(true));
        setReqLoading(false);
        navigate('/indexmanagement');
      }
    } catch (e: any) {
      console.log(e);
      setCustomIndexConfirm(false);
      setReqLoading(false);
      setIsOpenErrorPopup(true);
      if (
        e?.response?.data?.validationErrors &&
        e?.response?.data?.validationErrors[0]?.message[0] ===
          t('indexes.configureIndex.duplicateEmail')
      ) {
        setApiErrorMsg(e?.response?.data?.validationErrors[0]?.message[0]);
      } else {
        setApiErrorMsg(e?.response?.data?.meta?.message);
      }
      setTimeout(() => {
        setIsOpenErrorPopup(false);
      }, 4000);
    }
  };

  const cancelCreateIndexHandler = () => {
    trackCreateIndexModalClick(
      t('gaTracking.global.cancel'),
      t('gaTracking.customIndex.newIndexCancel')
    );
    setCustomIndexConfirm(false);
    //cancel for confirmation
  };

  const modalContent = () => {
    return (
      <>
        <Typography className="typo-text.body2_size rerun-popup-text-color">
          {t(`indexes.configureIndex.NotifyPopupDesc`)}
        </Typography>
        <Box sx={{width: '100%', marginTop: '20px'}}>
          <TextField
            value={emails}
            onChange={handleChange}
            fullWidth
            helperText={error || t('common.emailHelperText')}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              '& .MuiFormHelperText-root': {
                color: error ? '#f44336' : '',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#F2F4F7',
              },
            }}
            placeholder={t(`indexes.configureIndex.emailPlaceholder`)}
          />
        </Box>
      </>
    );
  };

  const handleChangeAnalyzers = (newAnalyzers: any) => {
    const result: any = Object.entries(newAnalyzers).map(
      ([field, analyzer]) => ({
        name: field,
        analyzer: analyzer,
      })
    );
    setNewAnalyzersList(result);
  };

  const handleSnackbarClose = () => {
    setIsOpenErrorPopup(false);
  };

  return (
    <div>
      <Typography
        className="typo-text headline3_size title_cf"
        style={{marginLeft: '160px'}}
      >
        {t(`indexes.configureIndex.heading`)}
      </Typography>
      <Typography
        className="typo-text body2_size configure-index-subheader"
        style={{marginLeft: '160px'}}
      >
        {t(`indexes.configureIndex.subheading`)}
      </Typography>
      <div className="configure-index-hr-line"></div>
      <div className="configure-index-table">
        <ConfigureIndexTable
          buildTypeList={buildTypeList}
          tableData={tableData}
          handleChangeAnalyzers={handleChangeAnalyzers}
        />
      </div>
      <Box className="index-action-container configure-index-footer">
        <div className="custom-index-action-button">
          <AurButton
            type="exit"
            tabIndex={0}
            label={t('buttons.cancel')}
            title={t('buttons.cancel')}
            size="Medium"
            onClick={() => cancelButtonHandler()}
            dataTestId={'closeBtn-test'}
            classNames="index-cancel-btn"
            ariaDescription={t(
              'accessibility.indexManagement.create.cancelHint'
            )}
          />
          <AurButton
            type="continue"
            label={t('buttons.continue')}
            title={t('buttons.continue')}
            size="Medium"
            tabIndex={0}
            onClick={() => {
              continueButtonHandler();
            }}
            dataTestId={'closeBtn-test'}
            ariaDescription={t(
              'accessibility.indexManagement.create.continue2Hint'
            )}
          />
        </div>
      </Box>
      {openPopup && (
        <AurPopup
          className="aur-unsaved-changes-popup index-popup cacel-btn-transparent"
          open={openPopup}
          onOk={handleOkButton}
          onCancel={closeButtonHandler}
          showPopupHeading={true}
          popupHeading={t(`indexes.configureIndex.NotifyCompleted`)}
          popupTitle={''}
          popupContent={modalContent()}
          contentType="element"
          showPopupTitle={true}
          showCloseIcon={true}
          labelActionCancel={t('common.clearAllPopup.buttonCancel')}
          labelActionOk={t('indexes.reRunPopup.continueBtn')}
          exitButtonHint={t('buttons.closeModalAndReturn')}
          continueButtonHint={t(`indexes.configureIndex.continueHint`)}
          closeIconHint={t('buttons.closeModalAndReturn')}
          continueButtonState={isButtonDisabled}
        />
      )}

      {cancelBtnPopup && (
        <AurPopup
          className="aur-unsaved-changes-popup add-doc-confirm-popup"
          open={cancelBtnPopup}
          onOk={handleContinueOptionSelected}
          onCancel={closeModalHandler}
          popupHeading={t('common.clearAllPopup.heading')}
          showPopupHeading={true}
          showPopupTitle={false}
          showCloseIcon={false}
          popupContent={t('indexes.customIndex.step1.confirmMessage')}
          labelActionCancel={t('common.clearAllPopup.buttonCancel')}
          labelActionOk={t('buttons.continue')}
          exitButtonHint={t('common.clearAllPopup.cacnelToClearHint')}
          continueButtonHint={t('common.clearAllPopup.confirmToContinue')}
          cancelButtonTitle=""
        />
      )}

      {customIndexConfirm && (
        <AurPopup
          className="aur-unsaved-changes-popup index-popup"
          open={customIndexConfirm}
          onOk={createIndexConfirmHandler}
          onCancel={cancelCreateIndexHandler}
          popupHeading={t(`indexes.configureIndex.createCustomIndex`)}
          showPopupHeading={true}
          showPopupTitle={true}
          popupTitle={''}
          showCloseIcon={true}
          popupContentClass="typo-text body2_size"
          popupContentParagraphCheck={true}
          popupContentParagraph={t('indexes.configureIndex.createCustomDesc')}
          labelActionCancel={t('common.clearAllPopup.buttonCancel')}
          labelActionOk={t(`indexes.configureIndex.createIndexPopupBtn`)}
          exitButtonHint={t('eca.newExport.cancel2Hint')}
          continueButtonHint={t(`indexes.configureIndex.continueHint`)}
          closeIconHint={t('eca.newExport.cancel2Hint')}
        />
      )}
      <div className="create-export-toast">
        <AurToast
          ariaLabel={t('common.toastAlert.toastAlertHint')}
          hideDuration={5000}
          open={isOpenErrorPopup}
          severity={'error'}
          horizontalPosition={'center'}
          verticalPosition={'bottom'}
          message={apiErrorMsg}
          onClose={() => handleSnackbarClose}
        />
      </div>
      <AurLoadingIndicator overlay={true} isOpen={reqLoading} />
    </div>
  );
};

export default ConfigureIndexStep4;
